import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Plus, Search, Trash2 } from 'lucide-react';
import {
  createLocationMarker,
  DEFAULT_LOCATION,
  normalizeLocation,
} from '../../lib/invitation';
import { BANCO_DAUNIA_CONTACTS } from '../../lib/bancoContacts';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ImageUpload } from './InvitationFormParts';
import InvitationMap from './InvitationMap';
import { cn } from '../../lib/utils';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const PHOTON_URL = 'https://photon.komoot.io/api/';
const SEARCH_TIMEOUT_MS = 6000;

function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`${label} timeout`)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

async function searchNominatim(query, signal) {
  const params = new URLSearchParams({
    q: query,
    format: 'json',
    addressdetails: '1',
    limit: '6',
    countrycodes: 'it',
    email: BANCO_DAUNIA_CONTACTS.email,
  });
  const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw new Error(`Nominatim ${res.status}`);
  const data = await res.json();
  return (data || []).map((item) => ({
    id: `nom-${item.place_id}`,
    label: item.display_name,
    lat: Number(item.lat),
    lng: Number(item.lon),
  }));
}

async function searchPhoton(query, signal) {
  const params = new URLSearchParams({
    q: query,
    limit: '6',
    lang: 'en',
  });
  const res = await fetch(`${PHOTON_URL}?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw new Error(`Photon ${res.status}`);
  const data = await res.json();
  return (data?.features || [])
    .map((feature, index) => {
      const [lng, lat] = feature.geometry?.coordinates || [];
      const p = feature.properties || {};
      const parts = [p.name, p.street, p.housenumber, p.city || p.town || p.village, p.state, p.country]
        .filter(Boolean);
      const label = parts.join(', ') || p.name;
      if (!label || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      if (p.countrycode && String(p.countrycode).toLowerCase() !== 'it') return null;
      return {
        id: `pho-${p.osm_id || index}`,
        label,
        lat: Number(lat),
        lng: Number(lng),
      };
    })
    .filter(Boolean);
}

/** Photon first: Nominatim often returns 403 to browser apps. */
async function searchAddresses(query, signal) {
  const q = (query || '').trim();
  if (q.length < 3) return [];

  try {
    const photon = await withTimeout(searchPhoton(q, signal), SEARCH_TIMEOUT_MS, 'Photon');
    if (photon.length > 0) return photon;
  } catch {
    // try Nominatim below
  }

  const nominatim = await withTimeout(searchNominatim(q, signal), SEARCH_TIMEOUT_MS, 'Nominatim');
  return nominatim;
}

function SuggestionsDropdown({ anchorRef, open, results, searching, onSelect }) {
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setCoords(null);
      return undefined;
    }
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, results.length, searching]);

  if (!open || !coords || typeof document === 'undefined') return null;

  return createPortal(
    <ul
      data-location-suggestions
      className="z-[200] max-h-56 overflow-auto rounded-lg border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)] shadow-lg"
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        width: coords.width,
      }}
      role="listbox"
    >
      {searching && results.length === 0 ? (
        <li className="px-3 py-2 text-sm ba-muted">Ricerca in corso…</li>
      ) : null}
      {results.map((item) => (
        <li key={item.id} role="option" aria-selected={false}>
          <button
            type="button"
            className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--ba-hover-subtle)]"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(item)}
          >
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />
            <span>{item.label}</span>
          </button>
        </li>
      ))}
      {!searching && results.length === 0 ? (
        <li className="px-3 py-2 text-sm ba-muted">Nessun indirizzo trovato</li>
      ) : null}
    </ul>,
    document.body
  );
}

function LocationPicker({ value, onChange, accentColor = '#F68B1F' }) {
  const location = normalizeLocation(value);
  const [query, setQuery] = useState(location.address || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [openResults, setOpenResults] = useState(false);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const wrapRef = useRef(null);
  const inputWrapRef = useRef(null);
  const suppressSearchRef = useRef(false);
  const searchSeqRef = useRef(0);

  useEffect(() => {
    // Do not overwrite what the user is typing.
    if (inputFocused) return;
    setQuery(location.address || '');
  }, [location.address, inputFocused]);

  useEffect(() => {
    if (!location.markers.length) {
      setSelectedMarkerId(null);
      return;
    }
    if (!selectedMarkerId || !location.markers.some((m) => m.id === selectedMarkerId)) {
      setSelectedMarkerId(location.markers[0].id);
    }
  }, [location.markers, selectedMarkerId]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        // Portal dropdown is outside wrapRef — ignore clicks on it via data attribute
        if (e.target?.closest?.('[data-location-suggestions]')) return;
        setOpenResults(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setSearching(false);
      setOpenResults(false);
      setError('');
      return undefined;
    }

    if (suppressSearchRef.current) {
      suppressSearchRef.current = false;
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    setOpenResults(true);
    setError('');
    const seq = ++searchSeqRef.current;

    debounceRef.current = window.setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const next = await searchAddresses(q, controller.signal);
        if (seq !== searchSeqRef.current) return;
        setResults(next);
        setError('');
        setOpenResults(true);
      } catch (err) {
        if (controller.signal.aborted || seq !== searchSeqRef.current) return;
        setError('Ricerca indirizzo non riuscita. Riprova tra poco.');
        setResults([]);
        setOpenResults(true);
      } finally {
        if (seq === searchSeqRef.current) setSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [query]);

  const commit = (nextLocation) => {
    onChange?.(normalizeLocation(nextLocation));
  };

  const update = (patch) => {
    commit({ ...location, ...patch });
  };

  const setMarkers = (markers) => {
    const next = markers
      .map((item) => createLocationMarker(item))
      .filter((m) => m.lat != null && m.lng != null);
    commit({
      ...location,
      markers: next,
      lat: next[0]?.lat ?? null,
      lng: next[0]?.lng ?? null,
    });
  };

  const selectResult = (item) => {
    const existing = location.markers;
    let nextMarkers;
    if (existing.length === 0) {
      const created = createLocationMarker({
        lat: item.lat,
        lng: item.lng,
        name: '',
        markerUrl: location.markerUrl || '',
      });
      nextMarkers = [created];
      setSelectedMarkerId(created.id);
    } else {
      const targetId = selectedMarkerId && existing.some((m) => m.id === selectedMarkerId)
        ? selectedMarkerId
        : existing[0].id;
      nextMarkers = existing.map((m) => (
        m.id === targetId ? { ...m, lat: item.lat, lng: item.lng } : m
      ));
      setSelectedMarkerId(targetId);
    }

    suppressSearchRef.current = true;
    setQuery(item.label);
    setResults([]);
    setOpenResults(false);
    setInputFocused(false);
    commit({
      ...location,
      address: item.label,
      showMap: true,
      markers: nextMarkers,
      lat: nextMarkers[0].lat,
      lng: nextMarkers[0].lng,
    });
  };

  const addMarkerAt = (lat, lng, name = '') => {
    const created = createLocationMarker({
      lat,
      lng,
      name,
      markerUrl: '',
    });
    const next = [...location.markers, created];
    setSelectedMarkerId(created.id);
    setMarkers(next);
  };

  const handleMapClick = ({ lat, lng }) => {
    if (selectedMarkerId && location.markers.some((m) => m.id === selectedMarkerId)) {
      setMarkers(
        location.markers.map((m) => (m.id === selectedMarkerId ? { ...m, lat, lng } : m))
      );
      return;
    }
    addMarkerAt(lat, lng);
  };

  const updateMarker = (id, patch) => {
    setMarkers(location.markers.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const removeMarker = (id) => {
    const next = location.markers.filter((m) => m.id !== id);
    setMarkers(next);
    if (selectedMarkerId === id) {
      setSelectedMarkerId(next[0]?.id || null);
    }
  };

  const clearLocation = () => {
    suppressSearchRef.current = true;
    onChange?.({ ...DEFAULT_LOCATION, markers: [] });
    setQuery('');
    setResults([]);
    setOpenResults(false);
    setSelectedMarkerId(null);
  };

  const hasMarkers = location.markers.length > 0;

  return (
    <div className="space-y-4" ref={wrapRef}>
      <div>
        <label className="ba-label" htmlFor="invitation-location-search">
          Luogo evento
        </label>
        <div className="relative" ref={inputWrapRef}>
          <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-[var(--ba-muted)]" />
          <Input
            id="invitation-location-search"
            className="pl-9"
            placeholder="Cerca un indirizzo (es. Via Roma 1, Foggia)"
            value={query}
            onChange={(e) => {
              suppressSearchRef.current = false;
              setQuery(e.target.value);
            }}
            onBlur={() => {
              setInputFocused(false);
              const next = query.trim();
              if (next !== (location.address || '').trim()) {
                update({ address: next });
              }
            }}
            onFocus={() => {
              setInputFocused(true);
              if (results.length > 0 || searching) setOpenResults(true);
            }}
            autoComplete="off"
          />
        </div>
        <SuggestionsDropdown
          anchorRef={inputWrapRef}
          open={openResults && query.trim().length >= 3}
          results={results}
          searching={searching}
          onSelect={selectResult}
        />
        <p className="mt-1 ba-muted">
          {searching
            ? 'Ricerca in corso…'
            : 'Seleziona un risultato per il luogo principale, oppure aggiungi marker dalla mappa.'}
        </p>
        {error && <p className="mt-1 text-sm text-brand-danger">{error}</p>}
      </div>

      {hasMarkers && (
        <>
          <InvitationMap
            location={{ ...location, showMap: true }}
            accentColor={accentColor}
            interactive
            heightClass="h-56"
            selectedMarkerId={selectedMarkerId}
            onMarkerClick={setSelectedMarkerId}
            onMapClick={handleMapClick}
          />
          <p className="text-xs ba-muted">
            Seleziona un marker dall&apos;elenco (o cliccandolo in mappa), poi clicca sulla mappa per spostarlo.
            Usa «Aggiungi marker» per crearne altri: saranno tutti visibili insieme nell&apos;invito.
          </p>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold">Marker sulla mappa</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 normal-case tracking-normal"
                onClick={() => {
                  const base = location.markers[0];
                  addMarkerAt(
                    (base?.lat ?? 41.462) + (Math.random() - 0.5) * 0.004,
                    (base?.lng ?? 15.545) + (Math.random() - 0.5) * 0.004
                  );
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                Aggiungi marker
              </Button>
            </div>

            {location.markers.map((marker, index) => {
              const selected = marker.id === selectedMarkerId;
              return (
                <div
                  key={marker.id}
                  className={cn(
                    'rounded-xl border p-3 transition-colors',
                    selected
                      ? 'border-brand-orange bg-[var(--ba-accent-orange-subtle)]'
                      : 'border-[var(--ba-surface-border)]'
                  )}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="text-left text-sm font-semibold"
                      onClick={() => setSelectedMarkerId(marker.id)}
                    >
                      Marker {index + 1}
                      {selected ? ' · selezionato' : ''}
                    </button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-brand-danger"
                      aria-label={`Rimuovi marker ${index + 1}`}
                      onClick={() => removeMarker(marker.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <label className="ba-label" htmlFor={`marker-name-${marker.id}`}>
                    Nome (opzionale)
                  </label>
                  <Input
                    id={`marker-name-${marker.id}`}
                    value={marker.name || ''}
                    placeholder="Es. Ingresso, Parcheggio…"
                    onChange={(e) => updateMarker(marker.id, { name: e.target.value })}
                    onFocus={() => setSelectedMarkerId(marker.id)}
                  />
                  <p className="mt-1 text-[11px] ba-muted">
                    {marker.lat.toFixed(5)}, {marker.lng.toFixed(5)}
                  </p>
                </div>
              );
            })}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-orange"
              checked={location.showMap !== false}
              onChange={(e) => update({ showMap: e.target.checked })}
            />
            Mostra la mini mappa nell&apos;invito pubblico
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-orange"
              checked={location.showDirections !== false}
              onChange={(e) => update({ showDirections: e.target.checked })}
            />
            Mostra il link &quot;Apri indicazioni&quot; nell&apos;invito
          </label>
          <ImageUpload
            label="Icona marker di default (opzionale)"
            hint="Usata per tutti i marker senza icona propria. PNG/JPG trasparente consigliato."
            value={location.markerUrl || ''}
            onChange={(v) => update({ markerUrl: v })}
            previewMaxH={48}
          />
          <Button type="button" variant="outline" className="gap-2" onClick={clearLocation}>
            <Trash2 className="h-4 w-4" />
            Rimuovi luogo
          </Button>
        </>
      )}
    </div>
  );
}

export default LocationPicker;
